import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { demoHeadersExtra, readDemoLeadRef } from "../constants/demoMode";

const TOKEN_KEY = "epp_user_token";
const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_V2 = `${API}/api/v2`;

const UserAuthContext = createContext(null);

export function UserAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  const fetchUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_V2}/auth/me`, {
        headers: { Authorization: `Bearer ${token}`, ...demoHeadersExtra() },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const checkEmailExists = useCallback(async (email) => {
    const res = await fetch(`${API_V2}/auth/check-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...demoHeadersExtra() },
      body: JSON.stringify({ email: String(email).trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const d = err.detail;
      const msg = Array.isArray(d) ? d[0]?.msg || "Requête invalide." : d;
      throw new Error(typeof msg === "string" ? msg : "Impossible de vérifier l’adresse e-mail.");
    }
    const data = await res.json();
    return Boolean(data.account_exists);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_V2}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...demoHeadersExtra() },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Email ou mot de passe incorrect.");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    const meRes = await fetch(`${API_V2}/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}`, ...demoHeadersExtra() },
    });
    if (meRes.ok) setUser(await meRes.json());
    return data;
  }, []);

  const register = useCallback(async (body) => {
    const res = await fetch(`${API_V2}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...demoHeadersExtra() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = Array.isArray(err.detail) ? err.detail[0]?.msg || err.detail : err.detail;
      throw new Error(typeof msg === "string" ? msg : "Inscription impossible.");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    const meRes = await fetch(`${API_V2}/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}`, ...demoHeadersExtra() },
    });
    if (meRes.ok) setUser(await meRes.json());
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const loginDemoClient = useCallback(async (email, password) => {
    const lr = readDemoLeadRef();
    const res = await fetch(`${API}/api/demo/client-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...demoHeadersExtra() },
      body: JSON.stringify({
        email: String(email).trim(),
        password: String(password),
        lead_ref: lr || undefined,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Identifiants démo incorrects.");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    const meRes = await fetch(`${API_V2}/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}`, ...demoHeadersExtra() },
    });
    if (meRes.ok) setUser(await meRes.json());
    return data;
  }, []);

  const api = useCallback(
    async (path, options = {}) => {
      const headers = { ...demoHeadersExtra(), ...options.headers };
      if (token) headers.Authorization = `Bearer ${token}`;
      let body = options.body;
      if (body && typeof body === "object" && !(body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(body);
      }
      const res = await fetch(`${API_V2}${path}`, { ...options, headers, body });
      if (res.status === 401) {
        logout();
        throw new Error("Session expirée.");
      }
      return res;
    },
    [token, logout]
  );

  return (
    <UserAuthContext.Provider
      value={{
        token,
        user,
        loading,
        checkEmailExists,
        login,
        register,
        logout,
        setUser,
        refreshUser: fetchUser,
        api,
        loginDemoClient,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error("useUserAuth must be used within UserAuthProvider");
  return ctx;
}
